/**
 * MAP-852: single source of truth for the "reachable" color palette used by
 * every color picker (v1 sidebar styles + user profile dialog, v2 inspector)
 * AND by AI color generation (node-style-defaults / ai-map-factory / the
 * prompt-generation, document-conversion, model-generation, chat skills).
 *
 * The palette is deliberately narrow: saturation is always 100% and the HSL
 * lightness stays at (or very near) 50%, so the wheel reads as a vivid RGB
 * rainbow - `hsl(60, 100%, 50%)` yellow stays yellow, `hsl(30, 100%, 50%)`
 * stays a bright orange.
 *
 * On top of that we apply only a GENTLE perceptual correction: plain HSL is
 * perceptually uneven (yellow reads far brighter than blue), so each hue's
 * lightness is nudged toward the wheel's average perceived lightness by a
 * fraction of the gap, and that nudge is hard-clamped to a few percentage
 * points. Naturally dark hues (blue/violet) come up slightly, naturally bright
 * ones (yellow/cyan) come down slightly, and nothing ever ends up muddy.
 *
 * NOTE: an earlier revision solved (by bisection) for the lightness whose WCAG
 * relative luminance exactly matched medium gray. That equalized brightness but
 * destroyed the palette - yellow collapsed to #838300 and orange to #c76400 -
 * which is why the correction is now bounded instead of exact.
 */

export interface RgbColor {
  r: number;
  g: number;
  b: number;
}

export interface HslColor {
  /** Degrees, [0, 360). */
  h: number;
  /** [0, 1]. */
  s: number;
  /** [0, 1]. */
  l: number;
}

/**
 * Medium gray anchor swatch (AC4): exact 50% gray, the achromatic entry of the
 * palette and the fallback whenever a color carries no usable hue.
 *
 * This is ALSO the system's general-purpose default gray: `DefaultValues`
 * (node / link / user color) imports it, so a freshly created node or link
 * lands exactly on the 9th swatch instead of adding a near-identical custom
 * one next to it. The old default was '#777777', close enough to look like a
 * duplicate but different enough to show up as an extra swatch.
 */
export const GRAY_SWATCH = '#7f7f7f';

/** The 8 core hue stops recreated as swatches. */
export const PALETTE_HUES = [0, 30, 50, 120, 180, 210, 270, 300] as const;

/** Second tier: midpoints of each adjacent core pair around the closed wheel. */
export const INTERMEDIATE_HUES = [15, 40, 85, 150, 195, 240, 285, 330] as const;

export function hexToRgb(hex: string): RgbColor | null {
  const match = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim());
  if (!match) return null;
  const int = parseInt(match[1], 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

export function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return (
    '#' +
    [clamp(r), clamp(g), clamp(b)]
      .map((v) => v.toString(16).padStart(2, '0'))
      .join('')
  );
}

/** h in degrees, s/l in [0, 1]. */
export function hslToRgb(h: number, s: number, l: number): RgbColor {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r1 = 0;
  let g1 = 0;
  let b1 = 0;
  if (hp < 1) {
    r1 = c; g1 = x; b1 = 0;
  } else if (hp < 2) {
    r1 = x; g1 = c; b1 = 0;
  } else if (hp < 3) {
    r1 = 0; g1 = c; b1 = x;
  } else if (hp < 4) {
    r1 = 0; g1 = x; b1 = c;
  } else if (hp < 5) {
    r1 = x; g1 = 0; b1 = c;
  } else {
    r1 = c; g1 = 0; b1 = x;
  }
  const m = l - c / 2;
  return {
    r: (r1 + m) * 255,
    g: (g1 + m) * 255,
    b: (b1 + m) * 255
  };
}

export function rgbToHsl(r: number, g: number, b: number): HslColor {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case rn:
        h = 60 * (((gn - bn) / d) % 6);
        break;
      case gn:
        h = 60 * ((bn - rn) / d + 2);
        break;
      default:
        h = 60 * ((rn - gn) / d + 4);
        break;
    }
  }
  if (h < 0) h += 360;
  return { h, s, l };
}

function linearizeChannel(c: number): number {
  const cs = c / 255;
  return cs <= 0.04045 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
}

/** WCAG-style relative luminance (0 = black, 1 = white), r/g/b in [0, 255]. */
export function relativeLuminance(r: number, g: number, b: number): number {
  return (
    0.2126 * linearizeChannel(r) +
    0.7152 * linearizeChannel(g) +
    0.0722 * linearizeChannel(b)
  );
}

/**
 * Perceived lightness (CIE L*, rescaled to [0, 1]) of a relative luminance.
 * Relative luminance is linear-light and wildly non-uniform to the eye; L* is
 * the roughly perceptually-uniform version of it, which is what the gentle
 * correction below reasons about.
 */
export function perceptualLightness(luminance: number): number {
  const y = Math.max(0, Math.min(1, luminance));
  const f = y > 0.008856 ? Math.cbrt(y) : 7.787 * y + 16 / 116;
  return (116 * f - 16) / 100;
}

/** Perceived lightness of the raw `hsl(h, 100%, 50%)` color for a hue. */
function pureHuePerceptualLightness(hueDeg: number): number {
  const rgb = hslToRgb(hueDeg, 1, 0.5);
  return perceptualLightness(relativeLuminance(rgb.r, rgb.g, rgb.b));
}

/**
 * Average perceived lightness of the raw, fully-saturated hue wheel (~0.674).
 * Hues are nudged TOWARD this, not toward medium gray - anchoring on gray is
 * what made the old palette dark, because the whole vivid wheel sits well
 * above gray in perceived lightness.
 */
export const REFERENCE_PERCEPTUAL_LIGHTNESS = (() => {
  let sum = 0;
  for (let h = 0; h < 360; h++) sum += pureHuePerceptualLightness(h);
  return sum / 360;
})();

/** Fraction of the perceived-lightness gap that is corrected (0 = raw HSL, 1 = fully equalized). */
export const PERCEPTUAL_CORRECTION_STRENGTH = 0.15;

/** Hard cap on how far HSL lightness may leave 50%, in absolute lightness units. */
export const MAX_LIGHTNESS_DEVIATION = 0.06;

const normalizedHueCache = new Map<number, string>();

/**
 * Fully-saturated color for a hue: HSL lightness 50%, moved by at most
 * `MAX_LIGHTNESS_DEVIATION` toward the wheel's mean perceived lightness. Dark
 * hues (blue ~240deg, violet ~270deg) get lifted a little, bright ones (yellow
 * ~60deg, cyan ~180deg) get pulled down a little, and every hue stays vivid:
 * at S=100% the RGB channel spread is `1 - |2L - 1|`, so a <=6-point lightness
 * move keeps chroma within ~12% of its maximum.
 */
export function normalizedColorForHue(hueDeg: number): string {
  const h = ((hueDeg % 360) + 360) % 360;
  const cacheKey = Math.round(h * 100); // cache at 0.01deg resolution
  const cached = normalizedHueCache.get(cacheKey);
  if (cached) return cached;

  const gap = REFERENCE_PERCEPTUAL_LIGHTNESS - pureHuePerceptualLightness(h);
  const deviation = Math.max(
    -MAX_LIGHTNESS_DEVIATION,
    Math.min(MAX_LIGHTNESS_DEVIATION, PERCEPTUAL_CORRECTION_STRENGTH * gap)
  );

  const rgb = hslToRgb(h, 1, 0.5 + deviation);
  const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
  normalizedHueCache.set(cacheKey, hex);
  return hex;
}

/** The gray anchor first, then the 8 hue swatches, in display order. */
export function getPredefinedSwatches(): string[] {
  return [GRAY_SWATCH, ...PALETTE_HUES.map((h) => normalizedColorForHue(h))];
}

const SWATCH_NAMES: Record<number, string> = {
  0: 'red', 30: 'orange', 50: 'yellow', 120: 'green', 180: 'cyan', 210: 'blue', 270: 'purple', 300: 'magenta',
};

/** The colours an AI may assign: exactly the predefined swatches of the color picker, named for the prompt. */
export function getAiColorChoices(): { name: string; hex: string }[] {
  return [
    { name: 'gray', hex: GRAY_SWATCH },
    ...PALETTE_HUES.map((h) => ({ name: SWATCH_NAMES[h], hex: normalizedColorForHue(h) })),
  ];
}

/**
 * CSS gradient for the hue slider track (AC3): stops come from the SAME
 * per-hue formula as the swatches, so the slider and the swatches can never
 * disagree about what a hue looks like.
 */
export function buildHueSliderGradient(stops = 24): string {
  const parts: string[] = [];
  for (let i = 0; i <= stops; i++) {
    const hue = (360 * i) / stops;
    const pct = (100 * i) / stops;
    parts.push(`${normalizedColorForHue(hue)} ${pct.toFixed(2)}%`);
  }
  return `linear-gradient(to right, ${parts.join(', ')})`;
}

/** Hue (degrees) of an arbitrary hex color, for positioning the slider thumb from the current value. */
export function hueFromHex(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  return rgbToHsl(rgb.r, rgb.g, rgb.b).h;
}

/**
 * Snaps an arbitrary hex color to the reachable palette (AC7): any hue, 100%
 * saturation, lightness per `normalizedColorForHue`. Low-saturation
 * or near-black/near-white input (no meaningful hue) snaps to the gray anchor
 * instead of an arbitrary hue.
 */
export function snapToReachableColor(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return GRAY_SWATCH;
  const { h, s, l } = rgbToHsl(rgb.r, rgb.g, rgb.b);
  if (s < 0.12 || l < 0.06 || l > 0.94) return GRAY_SWATCH;
  return normalizedColorForHue(h);
}

/**
 * Resolves an AI-provided color string to the reachable palette (AC7),
 * falling back to the gray anchor when the AI omitted a color. Single call
 * site for every AI skill that assigns a node/link/model color.
 */
export function resolveAiColor(raw?: string | null): string {
  if (!raw || typeof raw !== 'string') return GRAY_SWATCH;
  return snapToReachableColor(raw);
}

/** Case-insensitive hex color equality, tolerant of missing '#'/whitespace. */
export function isSameColor(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false;
  const norm = (v: string) => v.trim().replace(/^#/, '').toLowerCase();
  return norm(a) === norm(b);
}

function normalizeHue(hueDeg: number): number {
  return ((hueDeg % 360) + 360) % 360;
}

/** Shortest angular distance between two hues, in degrees ([0, 180]). */
export function hueDistance(a: number, b: number): number {
  const d = Math.abs(normalizeHue(a) - normalizeHue(b));
  return d > 180 ? 360 - d : d;
}

/** Midpoint of the widest gap on the closed wheel. Ties go to the lowest hue. */
export function midpointOfWidestHueGap(usedHues: number[]): number {
  const sorted = [...new Set(usedHues.map(normalizeHue))].sort((a, b) => a - b);
  if (sorted.length === 0) return PALETTE_HUES[0];
  if (sorted.length === 1) return normalizeHue(sorted[0] + 180);

  const closed = [...sorted, sorted[0] + 360];
  let widest = -1;
  let midpoint = sorted[0];
  for (let i = 0; i < closed.length - 1; i++) {
    const gap = closed[i + 1] - closed[i];
    if (gap > widest) {
      widest = gap;
      midpoint = normalizeHue(closed[i] + gap / 2);
    }
  }
  return midpoint;
}

/** Core hues, then intermediates, then gap bisection. Skips hues already in use. */
export function generationHueSequence(count: number, huesInUse: number[] = []): number[] {
  if (count <= 0) return [];
  const taken = huesInUse.map(normalizeHue);
  const isTaken = (h: number) => taken.some((t) => hueDistance(t, h) < 1);

  const out: number[] = [];
  const push = (h: number) => {
    out.push(h);
    taken.push(h);
  };

  for (const tier of [PALETTE_HUES, INTERMEDIATE_HUES]) {
    for (const h of tier) {
      if (out.length >= count) return out;
      if (!isTaken(h)) push(h);
    }
  }
  // Every named hue is spent: bisect the widest remaining gap, repeatedly.
  while (out.length < count) {
    const next = midpointOfWidestHueGap(taken);
    if (isTaken(next)) break; // wheel is saturated; nothing left to subdivide
    push(next);
  }
  // Degenerate guard only (wheel fully saturated): reuse from the front
  // rather than return short. Falls back to the core hues if nothing is free.
  const filler = out.length > 0 ? out : PALETTE_HUES.map((h) => h);
  for (let i = 0; out.length < count; i++) out.push(filler[i % filler.length]);
  return out;
}

export interface HuePool {
  /** Nearest still-free hue to `color`, as a palette hex. Returns `color` when spent. */
  take(color: string): string;
}

function freeHues(tier: readonly number[], taken: number[]): number[] {
  return tier.filter((h) => !taken.some((t) => hueDistance(t, h) < 1));
}

/**
 * Candidate hues as ordered tiers: every core hue is handed out before a single
 * intermediate one is, and bisected hues only appear once both named tiers are
 * spent. Within a tier the caller still gets the nearest hue to what it asked
 * for, so a green request lands on core green rather than on the next free slot.
 *
 * `count` only sizes the bisection tier. The named tiers are always offered in
 * full, so a two-category smap picks the two core hues that fit best instead of
 * the first two.
 */
export function createHuePool(count: number, huesInUse: number[] = []): HuePool {
  const taken = huesInUse.map(normalizeHue);
  const cores = freeHues(PALETTE_HUES, taken);
  const intermediates = freeHues(INTERMEDIATE_HUES, taken);
  const tiers: number[][] = [cores, intermediates];

  const named = cores.length + intermediates.length;
  if (count > named) {
    tiers.push(generationHueSequence(count, huesInUse).slice(named));
  }

  return {
    take(color: string): string {
      const tier = tiers.find((t) => t.length > 0);
      if (!tier) return color;

      const target = hueFromHex(color);
      let bestIndex = 0;
      let bestDistance = Number.POSITIVE_INFINITY;
      for (let i = 0; i < tier.length; i++) {
        const d = hueDistance(target, tier[i]);
        if (d < bestDistance) {
          bestDistance = d;
          bestIndex = i;
        }
      }

      const [hue] = tier.splice(bestIndex, 1);
      return normalizedColorForHue(hue);
    }
  };
}

/** The model decides which elements share a color; gray is passed through. */
export function assignGenerationColors(
  rawColors: (string | null | undefined)[],
  huesInUse: number[] = []
): string[] {
  const snapped = rawColors.map((raw) => resolveAiColor(raw));

  const distinct: string[] = [];
  for (const color of snapped) {
    if (isSameColor(color, GRAY_SWATCH)) continue;
    if (!distinct.some((seen) => isSameColor(seen, color))) distinct.push(color);
  }
  if (distinct.length === 0) return snapped;

  const pool = createHuePool(distinct.length, huesInUse);
  const assignment = new Map<string, string>();
  distinct.forEach((color) => assignment.set(color.toLowerCase(), pool.take(color)));

  return snapped.map(
    (color) => assignment.get(color.toLowerCase()) ?? color
  );
}

/** A legend entry the AI asked for. */
export interface GeneratedModelRequest {
  /** Legend label. Nodes claim their entry by repeating it in `modelName`. */
  name?: string | null;
  color?: string | null;
  /** Node shape, or link line style. '' / null means "varies" in the AI schema. */
  shape?: string | null;
}

/** A node the AI asked for. */
export interface GeneratedNodeRequest {
  color?: string | null;
  shape?: string | null;
  /** Containers are forced to gray and take no part in the assignment. */
  isContainer?: boolean;
  /** The legend entry this node belongs to, by name. */
  modelName?: string | null;
}

/** A legend entry the AI forgot, rebuilt from the nodes that claimed it. */
export interface SynthesizedModel {
  name: string;
  shape: string | null;
  color: string;
}

export interface GeneratedSmapColors {
  /** Colour per model, parallel to the input. Distinct across the models in use. */
  models: string[];
  /** Colour per node, parallel to the input. Equals its legend entry's colour. */
  nodes: string[];
  /** Parallel to `models`: false when no node claimed that entry, so drop it. */
  modelsInUse: boolean[];
  /** Entries to add: a named group of 2+ nodes the AI left out of the legend. */
  addedModels: SynthesizedModel[];
}

const MIN_NODES_PER_LEGEND_ENTRY = 2;

function styleKey(color: string, shape?: string | null): string {
  return `${color.toLowerCase()}|${(shape ?? '').toString().trim().toLowerCase()}`;
}

function nameKey(name?: string | null): string | null {
  const trimmed = (name ?? '').toString().trim().toLowerCase();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Legend and canvas resolved together: every node ends up on exactly its legend
 * entry's colour, containers are gray, and each entry in use gets its own hue.
 *
 * A node names its entry via `modelName`. That is authoritative and has NO
 * style fallback: a node whose category is missing from the legend becomes its
 * own group rather than inheriting whatever entry happens to share its colour,
 * which is what used to paint two unrelated categories the same. Nodes with no
 * `modelName` still fall back to matching on colour and shape.
 */
export function assignGeneratedSmapColors(
  models: GeneratedModelRequest[],
  nodes: GeneratedNodeRequest[],
  huesInUse: number[] = []
): GeneratedSmapColors {
  const modelSnapped = models.map((m) => resolveAiColor(m.color));
  const nodeSnapped = nodes.map((n) => resolveAiColor(n.color));

  const byName = new Map<string, number>();
  const byStyle = new Map<string, number>();
  const byColor = new Map<string, number>();
  models.forEach((model, i) => {
    const named = nameKey(model.name);
    if (named && !byName.has(named)) byName.set(named, i);
    const style = styleKey(modelSnapped[i], model.shape);
    if (!byStyle.has(style)) byStyle.set(style, i);
    const color = modelSnapped[i].toLowerCase();
    if (!byColor.has(color)) byColor.set(color, i);
  });

  const owner = new Array<number | undefined>(nodes.length);
  nodes.forEach((node, i) => {
    if (node.isContainer) return;
    const named = nameKey(node.modelName);
    if (named) {
      owner[i] = byName.get(named);
      return;
    }
    owner[i] =
      byStyle.get(styleKey(nodeSnapped[i], node.shape)) ??
      byColor.get(nodeSnapped[i].toLowerCase());
  });

  const modelsInUse = models.map((_, i) => owner.some((o) => o === i));

  // Nodes no legend entry covers, grouped by the category they named, or by
  // their colour when they named none.
  const groupOrder: string[] = [];
  const groupMembers = new Map<string, number[]>();
  nodes.forEach((node, i) => {
    if (node.isContainer || owner[i] !== undefined) return;
    if (isSameColor(nodeSnapped[i], GRAY_SWATCH) && !nameKey(node.modelName)) return;
    const key = nameKey(node.modelName) ?? `color:${nodeSnapped[i].toLowerCase()}`;
    if (!groupMembers.has(key)) {
      groupMembers.set(key, []);
      groupOrder.push(key);
    }
    (groupMembers.get(key) as number[]).push(i);
  });

  const usedModelCount = modelsInUse.filter(Boolean).length;
  const pool = createHuePool(usedModelCount + groupOrder.length, huesInUse);

  const modelColors = models.map((_, i) =>
    modelsInUse[i] ? pool.take(modelSnapped[i]) : modelSnapped[i]
  );

  const nodeColors = nodes.map((node, i) =>
    node.isContainer ? GRAY_SWATCH : nodeSnapped[i]
  );

  const addedModels: SynthesizedModel[] = [];
  for (const key of groupOrder) {
    const members = groupMembers.get(key) as number[];
    const color = pool.take(nodeSnapped[members[0]]);
    for (const i of members) nodeColors[i] = color;

    const named = nameKey(nodes[members[0]].modelName);
    if (named && members.length >= MIN_NODES_PER_LEGEND_ENTRY) {
      addedModels.push({
        name: (nodes[members[0]].modelName as string).trim(),
        shape: nodes[members[0]].shape ?? null,
        color
      });
    }
  }

  nodes.forEach((node, i) => {
    const index = owner[i];
    if (!node.isContainer && index !== undefined) nodeColors[i] = modelColors[index];
  });

  return { models: modelColors, nodes: nodeColors, modelsInUse, addedModels };
}
